import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook for managing delayed hover actions with timeout management.
 */
function useHoverDelay(
	callback: () => void,
	delay: number,
): {
	schedule: () => void;
	clear: () => void;
} {
	const timeoutRef = useRef<number | null>(null);

	const callbackRef = useRef(callback);

	useEffect(() => {
		callbackRef.current = callback;
	}, [callback]);

	const clear = useCallback((): void => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	const schedule = useCallback((): void => {
		clear();
		timeoutRef.current = window.setTimeout(() => {
			callbackRef.current();
		}, delay);
	}, [delay, clear]);

	useEffect(() => {
		return () => clear();
	}, [clear]);

	return { schedule, clear };
}

export { useHoverDelay };
